// Опорные точки вёрстки — единственный источник правды для JS.
// Зеркало styles/_breakpoints.scss: строки медиазапросов совпадают слово в слово,
// иначе раскладка из JS переключится не там, где переключаются стили.
//
// Ширины взяты из типовых разрешений и общепринятых наборов. Готовые сетки
// намеренно не привязаны к конкретным устройствам: Bootstrap 5 выбирает ступени
// 576/768/992/1200/1400 так, чтобы в них помещались контейнеры, кратные 12, и
// прямо оговаривает, что это «подмножество распространённых размеров, а не все
// устройства» (https://getbootstrap.com/docs/5.3/layout/breakpoints/); Tailwind
// с его 640/768/1024/1280/1536 пишет, что точки «навеяны распространёнными
// разрешениями» (https://tailwindcss.com/docs/responsive-design). Поэтому ниже —
// свои ступени, привязанные к тому, как админкой пользуются:
//   480  — телефон в портрете (самый широкий — 440px у iPhone 16 Pro Max)
//   1024 — планшет боком и нижняя граница ноутбука; выше этой точки раскладка
//          одна и та же, поэтому ноутбук, монитор и Full HD не разделяем
//
// Тип устройства по одной ширине не определяется: планшет боком (1194px) и
// ноутбук (1366px) — соседи. Различаем по media-фичам ввода: pointer описывает
// точность указателя (coarse — палец, fine — мышь), hover — умеет ли он
// наводиться без нажатия (https://developer.mozilla.org/en-US/docs/Web/CSS/@media/pointer).
// Пара `hover: none` + `pointer: coarse` — сенсорный экран.
//
// Высота отделяет телефон боком (400–430px) от планшета боком (от 700px).
// Считать высоту отдельной шкалой советует и Material 3: у него свои классы
// размера окна по высоте, и всё ниже 480dp он зовёт compact
// (https://m3.material.io/foundations/layout/applying-layout/window-size-classes).

export const BREAKPOINT_WIDTHS = {
  phone: 480,
  desktop: 1024
} as const

// Тип экрана. Сенсорные варианты не зависят от ширины окна (её у них не меняют),
// а варианты с мышью — наоборот, описывают именно окно браузера, а не монитор
export type Breakpoint =
  // Телефон в портрете и любое окно такой же ширины — до 480px включительно
  | 'phone'
  // Телефон боком: палец, горизонтально, высота экрана до 540px
  | 'phone-landscape'
  // Планшет в портрете: палец, вертикально, ширина от 481px
  | 'tablet'
  // Планшет боком: палец, горизонтально, высота от 541px
  | 'tablet-landscape'
  // Мышь, окно уже ноутбучного — 481–1023px: половина экрана или нетбук
  | 'compact'
  // Мышь, от 1024px: ноутбук, монитор и Full HD — раскладка у них общая
  | 'desktop'

export const TOUCH_QUERY = '(hover: none) and (pointer: coarse)'
export const LANDSCAPE_QUERY = '(orientation: landscape)'

export const BREAKPOINT_QUERIES: Record<Breakpoint, string> = {
  phone: `screen and (max-width: ${BREAKPOINT_WIDTHS.phone}px)`,
  'phone-landscape': `${TOUCH_QUERY} and ${LANDSCAPE_QUERY} and (min-width: ${BREAKPOINT_WIDTHS.phone + 1}px) and (max-height: 540px)`,
  'tablet-landscape': `${TOUCH_QUERY} and ${LANDSCAPE_QUERY} and (min-width: ${BREAKPOINT_WIDTHS.phone + 1}px) and (min-height: 541px)`,
  tablet: `${TOUCH_QUERY} and (orientation: portrait) and (min-width: ${BREAKPOINT_WIDTHS.phone + 1}px)`,
  compact: `screen and (min-width: ${BREAKPOINT_WIDTHS.phone + 1}px) and (max-width: ${BREAKPOINT_WIDTHS.desktop - 1}px)`,
  desktop: `screen and (min-width: ${BREAKPOINT_WIDTHS.desktop}px)`
}

// Сенсорные варианты проверяем раньше размерных, поэтому compact и desktop
// достаются только мыши и отдельного условия про указатель не требуют
export const BREAKPOINT_ORDER: Breakpoint[] = [
  'phone',
  'phone-landscape',
  'tablet-landscape',
  'tablet',
  'compact',
  'desktop'
]
